import { TextareaHTMLAttributes, forwardRef } from 'react';
import './Textarea.css';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  helperText?: string;
  fullWidth?: boolean;
  textareaClassName?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      label,
      error,
      helperText,
      fullWidth = false,
      id,
      className = '',
      textareaClassName = '',
      rows = 4,
      ...rest
    },
    ref,
  ) => {
    const hasError = error != null && error !== '';
    const textareaId =
      id ?? (label ? `textarea-${label.toLowerCase().replace(/\s+/g, '-')}` : undefined);

    const containerClass = [
      'textarea-field',
      fullWidth ? 'textarea-field--full-width' : '',
      hasError ? 'textarea-field--error' : '',
      rest.disabled ? 'textarea-field--disabled' : '',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const controlClass = [
      'textarea-field__control',
      hasError ? 'textarea-field__control--error' : '',
      textareaClassName,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={containerClass}>
        {label != null && (
          <label className="textarea-field__label" htmlFor={textareaId}>
            {label}
            {rest.required && (
              <span className="textarea-field__required" aria-hidden="true">
                {' '}*
              </span>
            )}
          </label>
        )}
        <textarea
          ref={ref}
          id={textareaId}
          rows={rows}
          className={controlClass}
          aria-describedby={
            hasError
              ? `${textareaId}-error`
              : helperText
              ? `${textareaId}-helper`
              : undefined
          }
          aria-invalid={hasError ? 'true' : 'false'}
          aria-required={rest.required ? 'true' : undefined}
          {...rest}
        />
        {hasError && (
          <span id={`${textareaId}-error`} className="textarea-field__error" role="alert">
            {error}
          </span>
        )}
        {helperText != null && !hasError && (
          <span id={`${textareaId}-helper`} className="textarea-field__helper">
            {helperText}
          </span>
        )}
      </div>
    );
  },
);

Textarea.displayName = 'Textarea';

export default Textarea;
